import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Caption } from '@/components/atoms/Eyebrow';
import { formatDateShortLocalized } from '@/utils/date/localized';

import { type TransferFact, transferAmount } from '../../helpers/transferFacts';

import styles from './FishGroupDetailModal.module.scss';

interface DetailTransfersProps {
    incoming: TransferFact[];
    outgoing: TransferFact[];
}

interface TransferListProps {
    title: string;
    transfers: TransferFact[];
    /** Which end of each transfer to name: where the fish came from, or where they went. */
    side: 'source' | 'destination';
}

function TransferList(props: TransferListProps) {
    const { title, transfers, side } = props;
    const { t, i18n } = useTranslation('fishGroups');
    const locale = i18n.language;
    const titleId = useId();
    if (transfers.length === 0) return null;

    return (
        <div className={styles.transferList}>
            <Caption as="h4" id={titleId}>
                {title}
            </Caption>
            <ul aria-labelledby={titleId}>
                {transfers.map((transfer) => {
                    const name = side === 'source' ? transfer.sourceName : transfer.destinationName;
                    const pen = side === 'source' ? transfer.sourcePen : transfer.destinationPen;
                    return (
                        <li key={transfer.key} className={styles.transferItem}>
                            <span className={styles.transferGroup}>
                                {pen
                                    ? t('siteActions.detail.transfers.groupInPen', { name, pen })
                                    : name}
                            </span>
                            <span className={styles.transferAmount}>
                                {t('siteActions.detail.transfers.amountOn', {
                                    amount: transferAmount(transfer, locale, t),
                                    date: formatDateShortLocalized(transfer.date, locale),
                                })}
                            </span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

/**
 * The fish that came into the group and left it, transfer by transfer, each with its count
 * and the share of its source group that moved.
 */
export default function DetailTransfers(props: DetailTransfersProps) {
    const { incoming, outgoing } = props;
    const { t } = useTranslation('fishGroups');
    const headingId = useId();
    if (incoming.length === 0 && outgoing.length === 0) return null;

    return (
        <section className={styles.transfers} aria-labelledby={headingId}>
            <h3 id={headingId} className={styles.registerTitle}>
                {t('siteActions.detail.transfers.title')}
            </h3>
            <TransferList
                title={t('siteActions.detail.transfers.incoming')}
                transfers={incoming}
                side="source"
            />
            <TransferList
                title={t('siteActions.detail.transfers.outgoing')}
                transfers={outgoing}
                side="destination"
            />
        </section>
    );
}
